import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SharedNav from '../../components/feature/SharedNav';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': `${SITE_URL}/medicare-kerrville-tx#breadcrumb`,
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Medicare Kerrville TX',
      item: `${SITE_URL}/medicare-kerrville-tx`,
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/medicare-kerrville-tx#webpage`,
  url: `${SITE_URL}/medicare-kerrville-tx`,
  name: 'Medicare Kerrville TX | Medicare Supplement, Advantage & Part D | West Insurance',
  description: 'Expert Medicare help in Kerrville, TX. Compare Medicare Supplement (Medigap), Medicare Advantage & Part D drug plans. Free consultation. Call (830) 203-6201. West Health Insurance.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: { '@id': `${SITE_URL}/medicare-kerrville-tx#breadcrumb` },
};

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Medicare Plans Kerrville TX',
  description: 'Expert Medicare guidance in Kerrville, TX. Compare Medicare Supplement (Medigap), Medicare Advantage, and Part D plans. Free consultations. Call (830) 203-6201.',
  provider: {
    '@type': 'InsuranceAgency',
    name: 'West Health Insurance',
    telephone: '+18302036201',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '243 La Reata Rd S',
      addressLocality: 'Kerrville',
      addressRegion: 'TX',
      postalCode: '78028',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Kerrville', containedInPlace: { '@type': 'State', name: 'Texas' } },
  url: `${SITE_URL}/medicare-kerrville-tx`,
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'When can I enroll in Medicare in Kerrville, TX?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can first enroll in Medicare during your Initial Enrollment Period, which starts 3 months before your 65th birthday and ends 3 months after. The Annual Enrollment Period runs from October 15 to December 7 each year. West Health Insurance helps Kerrville seniors navigate enrollment timing to avoid penalties.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between Medicare Supplement and Medicare Advantage in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Medicare Supplement (Medigap) plans work alongside Original Medicare (Parts A & B) to cover copays, coinsurance, and deductibles — giving you maximum flexibility in provider choice. Medicare Advantage (Part C) replaces Original Medicare with a bundled plan from a private insurer, often including dental, vision, and drug coverage. West Health Insurance helps Kerrville seniors compare both options.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Medicare cover prescription drugs in Kerrville, Texas?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Original Medicare (Parts A & B) does not cover most prescription drugs. You need Medicare Part D — a standalone prescription drug plan — or a Medicare Advantage plan that includes drug coverage. West Health Insurance helps Kerrville seniors find the Part D plan that covers their medications at the lowest cost.',
      },
    },
    {
      '@type': 'Question',
      name: 'Will I pay a penalty if I delay signing up for Medicare Part D in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. If you go 63 or more days without Medicare Part D or creditable drug coverage after you first become eligible, you may owe a late enrollment penalty added to your monthly premium. Our agents at West Health Insurance help Kerrville residents enroll on time and avoid costly penalties.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a cost for West Health Insurance to help me choose a Medicare plan in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Our Medicare consultation services are completely free for Kerrville seniors. We are paid by the insurance carriers, not by you. You get unbiased, expert guidance to compare Medicare Supplement, Medicare Advantage, and Part D plans available in Kerr County.',
      },
    },
  ],
};

const planTypes = [
  {
    name: 'Medicare Supplement (Medigap)',
    icon: 'ri-shield-plus-line',
    color: 'bg-[#0077B6]',
    text: 'white',
    parts: ['Covers Part A coinsurance', 'Covers Part B copays', 'No network restrictions', 'Use any doctor who accepts Medicare', 'Predictable out-of-pocket costs'],
    best: 'Best for: Seniors who travel or want maximum provider flexibility',
  },
  {
    name: 'Medicare Advantage (Part C)',
    icon: 'ri-hospital-line',
    color: 'bg-white',
    text: 'gray-900',
    parts: ['Replaces Original Medicare', 'Often $0 monthly premium', 'Includes dental, vision & hearing', 'Prescription drug coverage', 'Network-based care (HMO/PPO)'],
    best: 'Best for: Seniors looking for an all-in-one plan with low premiums',
  },
  {
    name: 'Medicare Part D',
    icon: 'ri-medicine-bottle-line',
    color: 'bg-white',
    text: 'gray-900',
    parts: ['Covers brand & generic drugs', 'Varies by formulary', 'Added to Original Medicare', 'Standalone drug plan', 'Annual enrollment available'],
    best: 'Best for: Seniors on Original Medicare who need Rx coverage',
  },
];

export default function MedicareKerrvillePage() {
  useEffect(() => {
    document.title = 'Medicare Kerrville TX | Medicare Supplement, Advantage & Part D | West Insurance';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Expert Medicare help in Kerrville, TX. Compare Medicare Supplement (Medigap), Medicare Advantage & Part D drug plans. Free consultation. Call (830) 203-6201. West Health Insurance.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/medicare-kerrville-tx`);

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.id = 'schema-medicare-service';
    s1.textContent = JSON.stringify(schemaData);
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    s2.type = 'application/ld+json';
    s2.id = 'schema-medicare-faq';
    s2.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(s2);

    const s3 = document.createElement('script');
    s3.type = 'application/ld+json';
    s3.id = 'schema-medicare-breadcrumb';
    s3.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(s3);

    const s4 = document.createElement('script');
    s4.type = 'application/ld+json';
    s4.id = 'schema-medicare-webpage';
    s4.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(s4);

    return () => {
      document.title = 'West Health Insurance Kerrville TX | Private Health, Group & Medicare';
      s1.parentNode?.removeChild(s1);
      s2.parentNode?.removeChild(s2);
      s3.parentNode?.removeChild(s3);
      s4.parentNode?.removeChild(s4);
    };
  }, []);

  const faqs = faqSchema.mainEntity;

  return (
    <div className="min-h-screen bg-white">
      <SharedNav />

      {/* Hero */}
      <section className="relative pt-20 min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8746586b98c88bbbb88ca413e9cdd850.jpg"
            alt="Medicare Plans for Seniors in Kerrville TX"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/40 to-black/20"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B6]/80 backdrop-blur-sm rounded-full mb-6">
              <i className="ri-heart-pulse-line text-white text-sm"></i>
              <span className="text-white text-sm font-medium">Medicare Guidance for Kerrville Seniors</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Medicare Plans<br />
              <span className="text-4xl md:text-5xl">in Kerrville, TX</span>
            </h1>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Navigating Medicare can be confusing. West Health Insurance gives Kerrville seniors expert, unbiased guidance to compare Medicare Supplement, Medicare Advantage, and Part D plans — at no cost to you.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link to="/#contact" className="px-8 py-4 bg-[#0077B6] text-white font-semibold rounded-lg hover:bg-[#005F8C] transition-all whitespace-nowrap cursor-pointer">
                Free Medicare Consultation
              </Link>
              <a href="tel:830-203-6201" className="px-8 py-4 bg-white text-[#0077B6] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                Call 830-203-6201
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Understanding Medicare in Kerrville, Texas
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Original Medicare (Parts A &amp; B) covers hospital stays and outpatient care, but it leaves significant gaps — including deductibles, copays, coinsurance, and most prescription drug costs. That's why most Kerrville seniors supplement their coverage.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                At <strong>West Health Insurance</strong>, our licensed Medicare agents help you understand all your options and make the best choice for your health and budget. Our consultation is always free — we're paid by the carriers, not you.
              </p>
              <div className="p-5 bg-[#F0F8FF] rounded-xl border-l-4 border-[#0077B6]">
                <p className="text-sm text-gray-700 font-medium">
                  <i className="ri-information-line text-[#0077B6] mr-2"></i>
                  Turning 65 soon? Your Initial Enrollment Period starts <strong>3 months before</strong> your birthday. Don't miss it — penalties apply!
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=friendly%20mature%20insurance%20agent%20talking%20with%20senior%20woman%20client%20reviewing%20Medicare%20plan%20documents%20in%20a%20cozy%20office%20with%20natural%20light%20plants%20on%20the%20desk%20professional%20but%20warm%20atmosphere%20blue%20and%20white%20color%20palette%20kerrville%20texas%20style&width=700&height=550&seq=medicare-agent-003&orientation=landscape"
                alt="Medicare Consultation with West Health Insurance in Kerrville TX"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Plan Types */}
      <section className="py-20 bg-[#F0F8FF]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Medicare Plan Options in Kerrville
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14">
            We compare all Medicare plan types available in Kerr County so you get the right coverage at the right price.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {planTypes.map((plan) => (
              <div key={plan.name} className={`rounded-2xl overflow-hidden ${plan.color === 'bg-[#0077B6]' ? 'bg-[#0077B6]' : 'bg-white'}`}>
                <div className="p-8">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-xl mb-5 ${plan.color === 'bg-[#0077B6]' ? 'bg-white/20' : 'bg-[#F0F8FF]'}`}>
                    <i className={`${plan.icon} text-2xl ${plan.color === 'bg-[#0077B6]' ? 'text-white' : 'text-[#0077B6]'}`}></i>
                  </div>
                  <h3 className={`text-xl font-bold mb-5 ${plan.color === 'bg-[#0077B6]' ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                  <ul className="space-y-3 mb-6">
                    {plan.parts.map((p) => (
                      <li key={p} className={`flex items-center gap-2 text-sm ${plan.color === 'bg-[#0077B6]' ? 'text-white/85' : 'text-gray-700'}`}>
                        <i className={`ri-check-circle-line ${plan.color === 'bg-[#0077B6]' ? 'text-white' : 'text-[#0077B6]'}`}></i>
                        {p}
                      </li>
                    ))}
                  </ul>
                  <p className={`text-xs font-semibold italic mb-6 ${plan.color === 'bg-[#0077B6]' ? 'text-white/70' : 'text-gray-500'}`}>{plan.best}</p>
                  <a
                    href="tel:830-203-6201"
                    className={`block text-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${plan.color === 'bg-[#0077B6]' ? 'bg-white text-[#0077B6] hover:bg-gray-100' : 'bg-[#0077B6] text-white hover:bg-[#005F8C]'}`}
                  >
                    Learn More
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            How Our Medicare Process Works
          </h2>
          <p className="text-gray-600 text-center mb-14">Getting the right Medicare plan in Kerrville is easier than you think.</p>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Free Consultation', desc: 'Call us or visit our Kerrville office. We\'ll learn about your doctors, prescriptions, and health needs.' },
              { step: '02', title: 'Plan Comparison', desc: 'We compare Medicare Supplement, Advantage, and Part D plans available in Kerr County.' },
              { step: '03', title: 'Personalized Recommendation', desc: 'We explain your best options clearly — no jargon, no pressure. You decide.' },
              { step: '04', title: 'Enrollment & Ongoing Support', desc: 'We handle your enrollment paperwork and remain your Medicare resource every year.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#0077B6] rounded-full mx-auto mb-4">
                  <span className="text-white font-bold text-lg">{s.step}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#F0F8FF]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Medicare FAQs for Kerrville Seniors
          </h2>
          <p className="text-gray-600 text-center mb-14">Common Medicare questions answered by our Kerrville insurance agents</p>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <details key={faq.name} className="group bg-white rounded-xl p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-gray-900 list-none cursor-pointer">
                  <span>{faq.name}</span>
                  <i className="ri-add-line text-[#0077B6] text-xl group-open:hidden"></i>
                  <i className="ri-subtract-line text-[#0077B6] text-xl hidden group-open:block"></i>
                </summary>
                <p className="mt-4 text-gray-600 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Get Free Medicare Help in Kerrville Today
          </h2>
          <p className="text-white/85 text-lg mb-8 max-w-xl mx-auto">
            Don't navigate Medicare alone. Our licensed agents in Kerrville, TX make it simple, clear, and completely free.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/#contact" className="px-8 py-4 bg-white text-[#0077B6] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
              Schedule a Free Consultation
            </Link>
            <a href="tel:830-203-6201" className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
              Call (830) 203-6201
            </a>
          </div>
          <div className="mt-10 flex items-center justify-center gap-8 flex-wrap">
            <Link to="/private-health-insurance-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Private Health Insurance
            </Link>
            <Link to="/group-health-insurance-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Group Health Insurance
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
